import React, { useEffect, useRef, useState } from 'react';
import { FiStar, FiTag, FiChevronDown, FiCheck, FiX } from 'react-icons/fi';
import { STORAGE_BOX_COLOR_TAGS } from '../constants';
import { useStorageBox } from '../StorageBoxContext';
import { colorTagDisplayName, colorTagHex, itemMetaSummaryText } from '../utils/itemMetaDisplay';
import { hasPredefinedStorageBoxFolderIcon } from '../utils/storageBoxFolderIcon';

function ColorTagSwatch({ colorTag, className = '', size = 'md' }) {
  const hex = colorTag ? colorTagHex(colorTag) : null;
  const sizeClass =
    size === 'sm'
      ? 'sb-color-swatch--sm'
      : size === 'palette'
        ? 'sb-color-swatch--palette'
        : 'sb-color-swatch--md';
  const modClass = colorTag ? '' : ' sb-color-swatch--none';
  const style = hex ? { backgroundColor: hex } : undefined;
  return (
    <span
      className={`sb-color-swatch ${sizeClass}${modClass} ${className}`.trim()}
      style={style}
      aria-hidden
    />
  );
}

export default function StorageBoxItemMetaControls({ item, compact = false, variant = 'inline' }) {
  const { readOnly, updateItemMeta } = useStorageBox();
  const [tagOpen, setTagOpen] = useState(false);
  const [folderColorOpen, setFolderColorOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState(item?.tagLabel || '');
  const [optionsOpen, setOptionsOpen] = useState(true);
  const labelInputRef = useRef(null);

  useEffect(() => {
    setLabelDraft(item?.tagLabel || '');
  }, [item?.itemId, item?.tagLabel]);

  useEffect(() => {
    setOptionsOpen(true);
    setTagOpen(false);
    setFolderColorOpen(false);
    setLabelOpen(false);
  }, [item?.itemId]);

  useEffect(() => {
    if (variant !== 'sidebar' || !labelOpen) return;
    labelInputRef.current?.focus();
    labelInputRef.current?.select();
  }, [variant, labelOpen, item?.itemId]);

  if (!item || readOnly || !item.canManage) return null;

  const toggleFavorite = () => {
    updateItemMeta(item, { isFavorite: !item.isFavorite });
  };

  const setColor = (colorTag) => {
    updateItemMeta(item, { colorTag });
  };

  const setFolderColor = (folderColor) => {
    updateItemMeta(item, { folderColor });
  };

  const showFolderColor =
    item.type === 'folder' && !hasPredefinedStorageBoxFolderIcon(item.name);

  const applyLabel = () => {
    updateItemMeta(item, { tagLabel: labelDraft.trim() });
    setLabelOpen(false);
  };

  const cancelLabel = () => {
    setLabelDraft(item?.tagLabel || '');
    setLabelOpen(false);
  };

  const deleteLabel = () => {
    setLabelDraft('');
    updateItemMeta(item, { tagLabel: '' });
    setLabelOpen(false);
  };

  const onLabelKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyLabel();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelLabel();
    }
  };

  const canDeleteLabel = Boolean(item.tagLabel || labelDraft.trim());

  const wrapClass = [
    'sb-item-meta',
    compact ? 'sb-item-meta--compact' : '',
    variant === 'sidebar' ? 'sb-item-meta--sidebar' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const controls = (
    <>
      <button
        type="button"
        className={`sb-item-meta__star${item.isFavorite ? ' sb-item-meta__star--on' : ''}`}
        onClick={toggleFavorite}
        aria-label={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        title={item.isFavorite ? 'Unfavorite' : 'Favorite'}
      >
        <FiStar aria-hidden fill={item.isFavorite ? 'currentColor' : 'none'} />
        <span className="sb-item-meta__action-text">Favourites</span>
      </button>
      <div className="sb-item-meta__colors">
        <button
          type="button"
          className="sb-item-meta__color-btn"
          onClick={() => {
            setLabelOpen(false);
            setFolderColorOpen(false);
            setTagOpen((v) => !v);
          }}
          aria-expanded={tagOpen}
          aria-label="Tags"
          title="Tags"
        >
          <ColorTagSwatch colorTag={item.colorTag || ''} />
          <span className="sb-item-meta__action-text">Tags</span>
        </button>
        {tagOpen ? (
          <div className="sb-item-meta__tags-panel sb-item-meta__tags-panel--colors-only">
            <div className="sb-item-meta__palette" role="listbox" aria-label="Choose tag colour">
              {STORAGE_BOX_COLOR_TAGS.map((tag) => (
                <button
                  key={tag.id || 'none'}
                  type="button"
                  role="option"
                  aria-selected={item.colorTag === tag.id}
                  aria-label={tag.label}
                  className={`sb-item-meta__palette-btn${
                    item.colorTag === tag.id ? ' sb-item-meta__palette-btn--active' : ''
                  }`}
                  title={tag.label}
                  onClick={() => setColor(tag.id)}
                >
                  <ColorTagSwatch colorTag={tag.id} size="palette" />
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      {showFolderColor ? (
        <div className="sb-item-meta__colors sb-item-meta__colors--folder">
          <button
            type="button"
            className="sb-item-meta__color-btn"
            onClick={() => {
              setLabelOpen(false);
              setTagOpen(false);
              setFolderColorOpen((v) => !v);
            }}
            aria-expanded={folderColorOpen}
            aria-label="Folder colour"
            title="Folder colour"
          >
            <ColorTagSwatch colorTag={item.folderColor || ''} />
            <span className="sb-item-meta__action-text">Folder</span>
          </button>
          {folderColorOpen ? (
            <div className="sb-item-meta__tags-panel sb-item-meta__tags-panel--colors-only">
              <div className="sb-item-meta__palette" role="listbox" aria-label="Choose folder colour">
                {STORAGE_BOX_COLOR_TAGS.map((tag) => (
                  <button
                    key={tag.id || 'none'}
                    type="button"
                    role="option"
                    aria-selected={item.folderColor === tag.id}
                    aria-label={tag.id ? tag.label : 'Default blue'}
                    className={`sb-item-meta__palette-btn${
                      item.folderColor === tag.id ? ' sb-item-meta__palette-btn--active' : ''
                    }`}
                    title={tag.id ? tag.label : 'Default blue'}
                    onClick={() => setFolderColor(tag.id)}
                  >
                    <ColorTagSwatch colorTag={tag.id} size="palette" />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="sb-item-meta__label-wrap">
        {variant === 'sidebar' && labelOpen ? (
          <div className="sb-item-meta__label-editor sb-item-meta__label-editor--inline-actions">
            <input
              ref={labelInputRef}
              type="text"
              className="sb-item-meta__label-input sb-item-meta__label-input--sidebar"
              placeholder="Enter label text"
              maxLength={64}
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onKeyDown={onLabelKeyDown}
              aria-label="Label text"
            />
            <div className="sb-item-meta__label-actions sb-item-meta__label-actions--icons">
              <button
                type="button"
                className="sb-item-meta__label-icon-btn sb-item-meta__label-icon-btn--save"
                onClick={applyLabel}
                title="Save"
                aria-label="Save label"
              >
                <FiCheck aria-hidden />
              </button>
              <button
                type="button"
                className="sb-item-meta__label-icon-btn"
                onClick={cancelLabel}
                title="Cancel"
                aria-label="Cancel label"
              >
                <FiX aria-hidden />
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={`sb-item-meta__label-btn${item.tagLabel ? ' sb-item-meta__label-btn--set' : ''}`}
              onClick={() => {
                setTagOpen(false);
                setFolderColorOpen(false);
                if (variant === 'sidebar') {
                  setLabelDraft(item?.tagLabel || '');
                  setLabelOpen(true);
                } else {
                  setLabelOpen((v) => !v);
                }
              }}
              aria-expanded={labelOpen}
              aria-label="Label"
              title={item.tagLabel || 'Label'}
            >
              <FiTag aria-hidden />
              <span className="sb-item-meta__action-text">Label</span>
              {item.tagLabel ? (
                <span className="sb-item-meta__label-value" title={item.tagLabel}>{item.tagLabel}</span>
              ) : null}
            </button>
            {labelOpen && variant !== 'sidebar' ? (
              <div className="sb-item-meta__tags-panel sb-item-meta__tags-panel--label">
                <div className="sb-item-meta__label-section">
                  <span className="sb-item-meta__label-heading">Label</span>
                  <div className="sb-item-meta__label-editor">
                    <input
                      type="text"
                      className="sb-item-meta__label-input"
                      placeholder="Enter label text"
                      maxLength={64}
                      value={labelDraft}
                      onChange={(e) => setLabelDraft(e.target.value)}
                      onKeyDown={onLabelKeyDown}
                      aria-label="Label text"
                    />
                    <div className="sb-item-meta__label-actions">
                      <button type="button" className="sb-item-meta__label-save-btn" onClick={applyLabel}>
                        Save
                      </button>
                      {canDeleteLabel ? (
                        <button type="button" className="sb-item-meta__label-delete-btn" onClick={deleteLabel}>
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );

  if (variant === 'sidebar') {
    return (
      <section className="sb-preview-options">
        <button
          type="button"
          className="sb-preview-options__toggle"
          aria-expanded={optionsOpen}
          aria-controls="sb-preview-options-panel"
          onClick={() => {
            setOptionsOpen((open) => {
              if (open) {
                setTagOpen(false);
                setFolderColorOpen(false);
                setLabelOpen(false);
              }
              return !open;
            });
          }}
        >
          <span className="sb-preview-options__toggle-label">Properties</span>
          <FiChevronDown
            className={`sb-preview-options__chevron${optionsOpen ? ' sb-preview-options__chevron--open' : ''}`}
            aria-hidden
          />
        </button>
        {optionsOpen ? (
          <div id="sb-preview-options-panel" className={wrapClass}>
            {controls}
          </div>
        ) : null}
      </section>
    );
  }

  return <div className={wrapClass}>{controls}</div>;
}

/** Text chips under the file name (list / favorites). */
export function StorageBoxItemIndicators({ item, inFavoritesView = false }) {
  const showFavorite = Boolean(item?.isFavorite || inFavoritesView);
  const hasColor = Boolean(item?.colorTag);
  const hasLabel = Boolean(item?.tagLabel);
  if (!item || (!showFavorite && !hasColor && !hasLabel)) return null;

  const colorName = hasColor ? colorTagDisplayName(item.colorTag) : '';

  return (
    <div className="sb-item-indicators" role="list" aria-label="Tags">
      {showFavorite ? (
        <span className="sb-item-indicators__chip sb-item-indicators__chip--fav" role="listitem">
          <FiStar className="sb-item-indicators__star" aria-hidden fill="currentColor" />
          <span className="sb-item-indicators__chip-label">Favorite</span>
        </span>
      ) : null}
      {hasColor ? (
        <span
          className={`sb-item-indicators__chip sb-item-indicators__chip--color sb-item-indicators__chip--${item.colorTag}`}
          role="listitem"
          title={item.tagLabel || colorName}
        >
          <ColorTagSwatch colorTag={item.colorTag} size="sm" className="sb-item-indicators__dot" />
          <span className="sb-item-indicators__chip-label">
            {item.tagLabel ? `${colorName}: ${item.tagLabel}` : colorName}
          </span>
        </span>
      ) : null}
      {hasLabel && !hasColor ? (
        <span className="sb-item-indicators__chip sb-item-indicators__chip--label" role="listitem" title={item.tagLabel}>
          <FiTag className="sb-item-indicators__tag-icon" aria-hidden />
          <span className="sb-item-indicators__chip-label">{item.tagLabel}</span>
        </span>
      ) : null}
    </div>
  );
}

/** Second line under the name (grid + list). */
export function StorageBoxItemMetaCaption({ item, inFavoritesView = false, className = '' }) {
  const text = itemMetaSummaryText(
    inFavoritesView && item && !item.isFavorite ? { ...item, isFavorite: true } : item
  );
  if (!text) return null;
  return (
    <span className={['sb-item-meta-caption', className].filter(Boolean).join(' ')} title={text}>
      {text}
    </span>
  );
}
